import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useRef,
  useMemo,
} from "react";
import { FilePlus, FilePlus2, Plus, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  usePagesStore,
  PagesStore,
  Page as Page,
} from "@/store/zustand/pagesStore";
import { StoreApi, UseBoundStore } from "zustand";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useCanvasId } from "@/utils/utils";
import {
  UpdatePageChange,
  CreatePageChange,
  DeletePageChange,
  DuplicatePageChange,
  MovePageChange,
} from "@/store/zustand/changeLedgerTypes";
import { generatePageFolderName, getUniqueName } from "@/utils/fileNameUtils";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { PageItemContextMenu } from "./PageItemContextMenu";
import { DeletePageDialog } from "./DeletePageDialog";
import path from "path-browserify";

/* -------------------------------------------------------------------------- */
/*                              UI Components                                 */
/* -------------------------------------------------------------------------- */

// Context for Storybook to provide isolated store instances
// Pages.tsx never creates this context, only consumes it if provided
type PagesStoreHook = UseBoundStore<StoreApi<PagesStore>>;
const PagesStoreContext = createContext<PagesStoreHook | null>(null);

// Export context for Storybook to use
export { PagesStoreContext };

type EphemeralPage = {
  id: string;
  name: string;
  isEphemeral: true;
};

export type PageItemProps = {
  page: Page | EphemeralPage;
  selectedPageId: string | null;
  onSelect: (pageId: string) => void;
  onPersist: (pageId: string) => void;
  onUnpersist: (pageId: string) => void;
  onRename: (pageId: string, newName: string, oldName: string) => void;
  onDelete: (pageId: string) => void;
  onDuplicate: (pageId: string) => void;
  shouldAutoEdit?: boolean;
  onEditStart?: (pageId: string) => void;
  onCancel?: () => void;
  disableDelete?: boolean;
};

export const PageItem = ({
  page,
  selectedPageId,
  onSelect,
  onPersist,
  onUnpersist,
  onRename,
  onDelete,
  onDuplicate,
  shouldAutoEdit = false,
  onCancel,
  disableDelete,
}: PageItemProps) => {
  const isSelected = selectedPageId === page.id;
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(page.name);
  const [isHovered, setIsHovered] = useState(false);

  const isEphemeral = "isEphemeral" in page;
  const isUnsaved =
    "folderPath" in page && page.folderPath?.includes("tempobook/");
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-enter edit mode if shouldAutoEdit is true
  React.useEffect(() => {
    if (shouldAutoEdit && !isEditing) {
      setIsEditing(true);
      setEditingName(page.name);
      // Auto-select text after the input is rendered
      setTimeout(() => {
        inputRef.current?.select();
      }, 0);
    }
  }, [shouldAutoEdit, isEditing, page.name]);

  const handleSave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onPersist(page.id);
    },
    [page.id, onPersist],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsEditing(true);
      setEditingName(page.name);
      setTimeout(() => {
        inputRef.current?.select();
      }, 0);
    },
    [page.name],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditingName(e.target.value);
    },
    [],
  );

  const saveName = useCallback(() => {
    const trimmedName = editingName.trim();
    const finalName = trimmedName || "Untitled Page";

    // Always trigger rename for ephemeral/unsaved pages (to create them) or if name changed
    if (isEphemeral || isUnsaved || finalName !== page.name) {
      onRename(page.id, finalName, page.name);
    }

    setIsEditing(false);
  }, [editingName, page.id, page.name, isEphemeral, isUnsaved, onRename]);

  const cancelEditing = useCallback(() => {
    if ("isEphemeral" in page) {
      onCancel?.();
    } else {
      setEditingName(page.name);
      setIsEditing(false);
    }
  }, [page, onCancel]);

  const handleNameBlur = useCallback(() => {
    saveName();
  }, [saveName]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveName();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEditing();
      }
    },
    [saveName, cancelEditing],
  );

  const startRename = useCallback(() => {
    setIsEditing(true);
    setEditingName(page.name);
    setTimeout(() => {
      inputRef.current?.select();
    }, 0);
  }, [page.name]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isEditing) {
        onSelect(page.id);
      }
    },
    [isEditing, onSelect, page.id],
  );

  const pageItem = (
    <div
      className={cn(
        "group min-w-full shrink-0 text-xs cursor-pointer flex items-center transition-colors select-none px-1.5 py-0.5",
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div
        className={cn(
          "flex w-full items-center gap-2 h-7 rounded px-2",
          isEditing
            ? "bg-background-secondary ring-2 ring-[#7C3AED]"
            : isSelected
              ? "bg-background-secondary"
              : "hover:bg-background-secondary",
        )}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editingName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            autoFocus
            className={cn(
              "truncate flex-1 bg-transparent border-none outline-hidden px-0",
              isUnsaved ? "text-text-tertiary" : "text-white",
            )}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={cn(
              "truncate flex-1",
              isUnsaved ? "text-secondary-800" : "text-white",
              isSelected && "font-medium",
            )}
            onDoubleClick={handleDoubleClick}
          >
            {page.name}
          </span>
        )}
        {isUnsaved && !isEditing && isHovered && (
          <button
            className="w-4 h-4 flex items-center justify-center text-text-tertiary hover:text-white transition-colors shrink-0"
            onClick={handleSave}
            data-tooltip-content="Save to Codebase"
            data-tooltip-id="tooltip"
          >
            <FilePlus2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  if ("isEphemeral" in page) {
    return pageItem;
  } else {
    return (
      <PageItemContextMenu
        page={page}
        onRename={startRename}
        onDelete={() => onDelete(page.id)}
        onDuplicate={() => onDuplicate(page.id)}
        onPersist={() => onPersist(page.id)}
        onUnpersist={() => onUnpersist(page.id)}
        disableDelete={disableDelete}
      >
        {pageItem}
      </PageItemContextMenu>
    );
  }
};

interface PagesViewProps {
  pages: Page[];
  selectedPageId: string | null;
  onNewPage: (pageId: string, pageName: string) => void;
  // PageItem handlers
  onSelectPage: (pageId: string) => void;
  onPersistPage: (pageId: string) => void;
  onUnpersistPage: (pageId: string) => void;
  onRenamePage: (pageId: string, newName: string) => void;
  onDeletePage: (pageId: string) => void;
  onDuplicatePage: (pageId: string) => void;
}

export const PagesView = ({
  pages,
  selectedPageId,
  onNewPage,
  onSelectPage,
  onPersistPage,
  onUnpersistPage,
  onRenamePage,
  onDeletePage,
  onDuplicatePage,
}: PagesViewProps) => {
  const [ephemeralUnnamedPage, setEphemeralUnnamedPage] =
    useState<EphemeralPage | null>(null);

  const onRenameSubmit = useCallback(
    (pageId: string, newName: string) => {
      if (pageId === ephemeralUnnamedPage?.id) {
        onNewPage(pageId, newName);
      } else {
        onRenamePage(pageId, newName);
      }
      setEphemeralUnnamedPage(null);
    },
    [onRenamePage, onNewPage, setEphemeralUnnamedPage, ephemeralUnnamedPage],
  );

  const handleNewUnnamedPage = useCallback(() => {
    const pageNumber = pages.length + 1;
    const baseName = `Page ${pageNumber}`;
    const existingNames = new Set(pages.map((p) => p.name));
    const newPageName = getUniqueName(baseName, existingNames, " ");

    // Create an ephemeral page (no backend files yet)
    const ephemeralPage: EphemeralPage = {
      id: uuidv4(),
      name: newPageName,
      isEphemeral: true,
    };

    setEphemeralUnnamedPage(ephemeralPage);
  }, [setEphemeralUnnamedPage, pages]);

  // Sort pages: persisted first (tempo/), then tempobook/, alphabetically within each group
  const sortedPages = useMemo(() => {
    const persistedPages: Page[] = [];
    const tempobookPages: Page[] = [];

    pages.forEach((page) => {
      const isPersisted =
        page.configPath && !page.configPath.includes("tempobook/");
      if (isPersisted) {
        persistedPages.push(page);
      } else {
        tempobookPages.push(page);
      }
    });

    // Sort each group alphabetically by configPath
    const sortByConfigPath = (a: Page, b: Page) => {
      const pathA = a.configPath || "";
      const pathB = b.configPath || "";
      return pathA.localeCompare(pathB);
    };

    persistedPages.sort(sortByConfigPath);
    tempobookPages.sort(sortByConfigPath);

    return [...persistedPages, ...tempobookPages];
  }, [pages]);

  const pagesToShow = [...sortedPages, ephemeralUnnamedPage].filter(
    (page) => page !== null,
  );

  return (
    <div className="flex w-full flex-col h-full">
      <div className="flex items-center justify-between px-3 h-10 shrink-0">
        <span className="text-xs font-bold text-white leading-none">Pages</span>
        <div className="flex items-center gap-1.5">
          <button
            className="w-5 h-5 flex items-center justify-center text-white hover:bg-background-secondary rounded cursor-pointer"
            onClick={handleNewUnnamedPage}
            title="New Page"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="relative flex flex-1 min-h-0">
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent hover:scrollbar-thumb-border/80">
          {pagesToShow.map((page) => (
            <PageItem
              key={page.id}
              page={page}
              selectedPageId={selectedPageId}
              onSelect={onSelectPage}
              onPersist={onPersistPage}
              onUnpersist={onUnpersistPage}
              onRename={onRenameSubmit}
              onDelete={onDeletePage}
              onDuplicate={onDuplicatePage}
              shouldAutoEdit={page.id === ephemeralUnnamedPage?.id}
              onCancel={() => {
                if (page.id === ephemeralUnnamedPage?.id) {
                  setEphemeralUnnamedPage(null);
                }
              }}
              disableDelete={pages.length <= 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Pages                                    */
/* -------------------------------------------------------------------------- */

const Pages = React.memo(() => {
  const contextStore = useContext(PagesStoreContext);
  const globalStore = usePagesStore;
  const useStore = contextStore || globalStore;
  const canvasId = useCanvasId();

  const pages = useStore((state) => state.pages[canvasId] || []);
  const tempobookPagesDir = useStore(
    (state) => state.tempobookPagesDirs[canvasId] || "",
  );
  const selectedPageId = useStore(
    (state) => state.selectedPageId[canvasId] || null,
  );
  const setSelectedPageId = useStore((state) => state.setSelectedPageId);
  const addPage = useStore((state) => state.addPage);
  const updatePage = useStore((state) => state.updatePage);

  const executeCommand = useDesignCommandManager(
    (state) => state.executeCommand,
  );

  const [deletePageCandidate, setDeletePageCandidate] = useState<{
    id: string;
    name: string;
    configPath: string;
  } | null>(null);

  const changePagePersistence = useCallback(
    (pageId: string, direction: "persist" | "unpersist") => {
      const page = pages.find((p) => p.id === pageId);
      if (!page || !page.configPath) return;

      const pageConfigPath = page.configPath;

      // Determine if this is a folder-based page (has parent directory between pages/ and the file)
      const pathParts = pageConfigPath.split("/");
      const pagesIndex = pathParts.lastIndexOf("pages");
      const isFolderBased =
        pagesIndex !== -1 && pathParts.length > pagesIndex + 2;

      // Compute source and destination paths
      let sourcePath: string;
      let destPath: string;
      let activityType: "PERSIST_PAGE" | "UNPERSIST_PAGE";

      if (direction === "persist") {
        // Persist: move from tempobook to tempo
        activityType = "PERSIST_PAGE";
        if (isFolderBased) {
          // Folder-based: move the parent directory
          // e.g., .../tempobook/pages/my-page/index.page.json -> move .../tempobook/pages/my-page
          const folderPath = pathParts.slice(0, -1).join("/");
          sourcePath = folderPath;
          destPath = folderPath.replace(/\/tempobook\//, "/tempo/");
        } else {
          // Standalone file: move just the file
          // e.g., .../tempobook/pages/my-page.page.json
          sourcePath = pageConfigPath;
          destPath = pageConfigPath.replace(/\/tempobook\//, "/tempo/");
        }
      } else {
        // Unpersist: move from tempo to tempobook
        activityType = "UNPERSIST_PAGE";
        if (isFolderBased) {
          // Folder-based: move the parent directory
          // e.g., .../tempo/pages/my-page/index.page.json -> move .../tempo/pages/my-page
          const folderPath = pathParts.slice(0, -1).join("/");
          sourcePath = folderPath;
          destPath = folderPath.replace(/\/tempo\//, "/tempobook/");
        } else {
          // Standalone file: move just the file
          // e.g., .../tempo/pages/my-page.page.json
          sourcePath = pageConfigPath;
          destPath = pageConfigPath.replace(/\/tempo\//, "/tempobook/");
        }
      }

      const change = new MovePageChange({
        canvasId,
        pageId,
        pageConfigPath,
        sourcePath,
        destPath,
        activityType,
      });
      executeCommand(change);
    },
    [pages, canvasId, executeCommand],
  );

  const handlePersist = useCallback(
    (pageId: string) => {
      changePagePersistence(pageId, "persist");
    },
    [changePagePersistence],
  );

  const handleUnpersist = useCallback(
    (pageId: string) => {
      changePagePersistence(pageId, "unpersist");
    },
    [changePagePersistence],
  );

  const handleNewPage = useCallback(
    (pageId: string, pageName: string) => {
      // This is an ephemeral page - create it now with the final name
      // Compute the config path: tempobook/pages/[FolderName-idPrefix]/index.page.json
      const folderName = generatePageFolderName(pageName, pageId);
      const pageConfigPath = path.join(
        tempobookPagesDir,
        `${folderName}/index.page.json`,
      );

      const newPage = {
        id: pageId,
        name: pageName,
        storyboards: [],
        configPath: pageConfigPath,
        folderPath: path.dirname(pageConfigPath),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      addPage(canvasId, newPage);
      setSelectedPageId(canvasId, newPage.id);

      const change = new CreatePageChange({
        canvasId,
        page: {
          id: newPage.id,
          name: newPage.name,
        },
        configPath: newPage.configPath,
      });
      executeCommand(change);
    },
    [canvasId, pages, addPage, setSelectedPageId],
  );

  const handleRename = useCallback(
    (pageId: string, newName: string) => {
      const page = pages.find((p) => p.id === pageId);
      if (!page) {
        console.error("Cannot rename page: page not found");
        return;
      }
      const oldName = page.name;

      let folderRenameInfo: { oldFolderPath?: string; newFolderPath?: string } =
        {};

      // Check if we should rename the folder
      if (page.folderPath) {
        const currentFolderName = page.folderPath.split("/").pop() || "";
        const idPrefix = pageId.split("-")[0];

        // Check if folder matches our pattern: {Name}-{idPrefix}
        const patternMatch = currentFolderName.match(
          new RegExp(`^(.+)-${idPrefix}$`),
        );

        if (patternMatch) {
          // Folder follows our pattern - compute new folder name
          const newFolderName = generatePageFolderName(newName, pageId);

          // Only rename if folder name would actually change
          if (currentFolderName !== newFolderName) {
            const parentPath = page.folderPath.replace(/\/[^/]+$/, "");
            folderRenameInfo = {
              oldFolderPath: page.folderPath,
              newFolderPath: `${parentPath}/${newFolderName}`,
            };
          }
        }
      }

      // This is a persisted page - rename it
      const change = new UpdatePageChange({
        canvasId,
        pageId,
        pageConfigPath: page.configPath,
        updates: { name: newName },
        previousValues: { name: oldName },
        ...folderRenameInfo,
      });

      executeCommand(change);
    },
    [canvasId, pages, updatePage, executeCommand],
  );

  const handleDelete = useCallback(
    (pageId: string) => {
      const page = pages.find((p) => p.id === pageId);
      if (!page) {
        console.error("Cannot delete page: page not found");
        return;
      }

      if (!page.configPath) {
        console.error("Cannot delete page: page has no config path");
        toast.error("Cannot delete unsaved page");
        return;
      }

      setDeletePageCandidate({
        id: pageId,
        name: page.name,
        configPath: page.configPath,
      });
    },
    [pages],
  );

  const confirmDelete = useCallback(() => {
    if (!deletePageCandidate) return;

    const change = new DeletePageChange({
      canvasId,
      pageId: deletePageCandidate.id,
      pageConfigPath: deletePageCandidate.configPath,
    });
    executeCommand(change);

    // If this was the selected page, select another
    if (selectedPageId === deletePageCandidate.id && pages.length > 1) {
      const remainingPages = pages.filter(
        (p) => p.id !== deletePageCandidate.id,
      );
      if (remainingPages.length > 0) {
        setSelectedPageId(canvasId, remainingPages[0].id);
      }
    }

    setDeletePageCandidate(null);
  }, [
    deletePageCandidate,
    canvasId,
    pages,
    selectedPageId,
    executeCommand,
    setSelectedPageId,
  ]);

  const handleDuplicate = useCallback(
    (pageId: string) => {
      const page = pages.find((p) => p.id === pageId);
      if (!page || !page.configPath) {
        toast.error("Cannot duplicate page");
        return;
      }

      const existingNames = new Set(pages.map((p) => p.name));
      const newPageName = getUniqueName(page.name, existingNames, " ");

      const change = new DuplicatePageChange({
        canvasId,
        sourcePageConfigPath: page.configPath,
        newPageId: uuidv4(),
        newPageName,
      });
      executeCommand(change);
    },
    [pages, canvasId, executeCommand],
  );

  return (
    <>
      <PagesView
        pages={pages}
        selectedPageId={selectedPageId}
        onNewPage={handleNewPage}
        onSelectPage={(pageId) => setSelectedPageId(canvasId, pageId)}
        onPersistPage={handlePersist}
        onUnpersistPage={handleUnpersist}
        onRenamePage={handleRename}
        onDeletePage={handleDelete}
        onDuplicatePage={handleDuplicate}
      />
      <DeletePageDialog
        open={deletePageCandidate !== null}
        onOpenChange={(open) => !open && setDeletePageCandidate(null)}
        pageName={deletePageCandidate?.name ?? ""}
        onConfirm={confirmDelete}
      />
    </>
  );
});

Pages.displayName = "Pages";

export default Pages;
